'use client'

import cn from 'classnames'
import {
  Form,
  Formik,
  FormikHelpers,
  FormikTouched,
  validateYupSchema,
  yupToFormErrors,
} from 'formik'
import _ from 'lodash'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'

import {Badge, BadgeColor} from '@/components/common/badges/Badge'
import {AffiliateBanner} from '@/components/common/banner/AffiliateBanner'
import {Text} from '@/components/common/content/Text'
import {ReassuranceBanner} from '@/components/common/form/banner/ReassuranceBanner'
import {FormLayout} from '@/components/common/form/FormLayout'
import {FormStep} from '@/components/common/form/FormStep'
import {BeforeCloseModal} from '@/components/common/modal/BeforeCloseModal'
import {usePreferences} from '@/components/context/Preferences'
import {useUserData} from '@/components/context/UserData'
import {FormErrorScroll} from '@/components/form/FormErrorScroll'
import {MarketsModalProvider} from '@/components/form/markets/context/MarketsModalContext'
import HiddenField from '@/components/inputs/HiddenField'
import {Page} from '@/components/Page'
import {MultiCountryDiscountPopup} from '@/components/trademark-verification/popup/MultiCountryDiscountPopup'
import {Summary} from '@/components/trademark-verification/summary/Summary'
import {TrademarkVerificationFields} from '@/components/trademark-verification/types'
import {UserLocaleHiddenFields} from '@/components/user/UserLocaleHiddenFields'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {useTrademarkVerificationSteps} from '@/content/trademarkVerificationSteps'
import {useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {REST_METHODS} from '@/utils/api/types'
import {TrademarkType} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {serializeValues} from '@/utils/form'
import {submitWithButtonStatus} from '@/utils/formikHelpers'
import {CoreRegistrationFieldsFragment} from '@/utils/graphqlSdk'
import {useRouter} from '@/utils/hooks/useRouter'
import {ServiceType} from '@/utils/registration-request/types'
import {routeTo} from '@/utils/router'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {getCampaignsData, useLogFirstFormInteraction} from '@/utils/tracking/helpers'
import {isAdminRole} from '@/utils/user/helpers'
import {useInvalidateBrandQueries} from '@/utils/user-portal/dataFetchers/brand'
import {
  clientTransformVerificationRequestBodySchema,
  clientVerificationRequestBodySchema,
} from '@/utils/validations/common/trademarkVerificationRequest'

type VerificationFormProps = {
  countries: Jurisdiction[]
  ssrMobileDetected: boolean
  amazonBrandRegistry: boolean
  hasPriorRegistrationRequest: boolean
}

const addClosedPopups = (closedPopups: string[], popupKey: string) =>
  closedPopups.includes(popupKey) ? [...closedPopups] : [...closedPopups, popupKey]

const VerificationForm: FC<VerificationFormProps> = ({
  countries,
  ssrMobileDetected,
  amazonBrandRegistry,
  hasPriorRegistrationRequest,
}) => {
  const {t} = useTranslation()

  const router = useRouter()
  const {country, affiliate, setAffiliate} = usePreferences()
  const {user, isLoggedIn} = useUserData()
  const isAdmin = isAdminRole(user)
  const isLoggedInCustomer = isLoggedIn && !isAdmin
  const isReturningCustomer = isLoggedInCustomer && hasPriorRegistrationRequest
  const validationContext = useMemo(() => ({isLoggedIn, isAdmin}), [isLoggedIn, isAdmin])
  const {lawyerCheckTitle, reassuranceBanner, freeLabel} = useTrademarkVerificationContent()
  const invalidateBrandQueries = useInvalidateBrandQueries()
  const [closedPopups, setClosedPopups] = useState<string[]>([])
  const [isReassuranceBannerVisible, setIsReassuranceBannerVisible] = useState(true)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {steps} = useTrademarkVerificationSteps({
    ssrMobileDetected,
    submitError,
    isReturningCustomer,
  })
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [lastVisitedStep, setLastVisitedStep] = useState<number>(0)

  const logFirstFormInteraction = useLogFirstFormInteraction('Verification touched')

  useEffect(() => {
    analytics.track('Started Verification', {
      countries: countries.length || undefined,
    })
    analytics.track(`Verification step ${currentStep + 1}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setVerificationStep = useCallback(
    (step: number, values: TrademarkVerificationFields) => {
      analytics.track(`Verification step ${step + 1}`, {values})
      setCurrentStep(step)
      setSubmitError(null)
    },
    [setSubmitError],
  )

  const handleNext = useCallback(
    (
      setTouched: (touched: FormikTouched<TrademarkVerificationFields>) => void,
      values: TrademarkVerificationFields,
    ) => {
      const nextStep = currentStep + 1
      nextStep > 1 && setClosedPopups(addClosedPopups(closedPopups, 'discount'))
      currentStep === lastVisitedStep && setLastVisitedStep(nextStep)
      setVerificationStep(nextStep, values)
      setTouched({})
    },
    [currentStep, lastVisitedStep, setVerificationStep, setLastVisitedStep, closedPopups],
  )

  const handleSubmit = useCallback(
    async (
      values: TrademarkVerificationFields,
      setStatus: FormikHelpers<TrademarkVerificationFields>['setStatus'],
    ) => {
      if (values.create_account) {
        analytics.track('Account created verification')
      }

      if (submitError) {
        setSubmitError(null)
      }

      await submitWithButtonStatus<CoreRegistrationFieldsFragment>({
        setStatus,
        onSubmit: async () => {
          const validatedData = clientTransformVerificationRequestBodySchema.validateSync(values, {
            context: validationContext,
          })

          const verificationData = {
            // removing undefined values due to this bug and inconsistency in yup https://github.com/jquense/yup/issues/1491
            ..._.omitBy(validatedData, _.isUndefined),
            analytics_data: getCampaignsData(),
            ...(router.query?.brandId ? {brand_id: router.query.brandId} : {}),
          }

          const response = await fetch(routeTo.api.request.create(amazonBrandRegistry), {
            method: REST_METHODS.POST,
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(await serializeValues(verificationData)),
          })

          return response
        },
        onSuccess: async ({id}) => {
          invalidateBrandQueries()

          if (values.skip_verification) {
            await router.push(routeTo.registrationRequest.form(id))
          } else {
            await router.push(routeTo.verification.success(id, values.create_account))
          }

          analytics.track('Submit Verification', {
            registrationId: id,
            countries: values.jurisdictions,
            email: values.contact_email,
            values,
          })
          setAffiliate(null)
        },
        onError: (errorKey) => {
          analytics.track('Failed Verification', {registrationId: null})
          setSubmitError(errorKey)
        },
        errorPath: '/verification/index',
        checkVpnError: true,
      })
    },
    [
      router,
      amazonBrandRegistry,
      invalidateBrandQueries,
      setAffiliate,
      setSubmitError,
      submitError,
      validationContext,
    ],
  )

  const submitForm = async (
    values: TrademarkVerificationFields,
    {setTouched, setStatus}: FormikHelpers<TrademarkVerificationFields>,
  ) => {
    if (steps[currentStep].isLastStep) {
      await handleSubmit(values, setStatus)
    } else {
      handleNext(setTouched, values)
    }
  }

  const reassuranceBannerDescription = isReturningCustomer
    ? user?.profile?.firstname
      ? t(reassuranceBanner.introDescriptionReturningClientWithName, {name: user.profile.firstname})
      : t(reassuranceBanner.introDescriptionReturningClient)
    : t(reassuranceBanner.introDescription)

  return (
    <>
      <MarketsModalProvider>
        <Page
          hasFooter={false}
          navigationLabel={t(lawyerCheckTitle)}
          navigationBadge={<Badge title={t(freeLabel)} color={BadgeColor.green} />}
        >
          <Formik<TrademarkVerificationFields>
            initialValues={{
              ...clientVerificationRequestBodySchema.getDefault(),
              jurisdictions: countries,
              // just due to typescript
              type: TrademarkType.image,
              ...(ssrMobileDetected ? {type: 'text', typeValue: 'text'} : {}),
            }}
            validate={async (formValues) => {
              try {
                await validateYupSchema(
                  formValues,
                  steps[currentStep].validationSchema,
                  true,
                  validationContext,
                )
              } catch (err) {
                return yupToFormErrors(err)
              }
              return {}
            }}
            onSubmit={submitForm}
            validateOnMount={true}
          >
            {({values, dirty, setStatus}) => {
              logFirstFormInteraction(dirty)

              return (
                <Form encType="multipart/form-data">
                  <UserLocaleHiddenFields />
                  <HiddenField name="country" value={country} />
                  <HiddenField name="service_type" value={ServiceType.registrationB2C} />
                  {affiliate && (
                    <>
                      <HiddenField name="affiliate" value={affiliate} />
                      <div className="desktop">
                        <AffiliateBanner
                          fullWidth
                          trackedEventParameters={{position: 'Verification form'}}
                          isDiscountApplied={values.jurisdictions.length > 0}
                        />
                      </div>
                    </>
                  )}
                  <FormLayout
                    content={
                      <div>
                        {affiliate && (
                          <div className="mobile">
                            <AffiliateBanner
                              fullWidth
                              trackedEventParameters={{position: 'Verification form'}}
                              isDiscountApplied={values.jurisdictions.length > 0}
                            />
                          </div>
                        )}
                        {steps.map(({title, content, validationSchema}, i) => {
                          const isCurrent = currentStep === i
                          const isCompleted = validationSchema.isValidSync(values, {
                            context: validationContext,
                          })
                          const isTouched = i <= lastVisitedStep

                          return (
                            <FormStep
                              key={title}
                              stepNumber={i + 1}
                              title={t(title)}
                              isCurrent={isCurrent}
                              onClick={() => {
                                setVerificationStep(i, values)
                                setStatus(null)
                                analytics.track(
                                  `Click on Edit button: from current step ${
                                    currentStep + 1
                                  } to step ${i + 1}`,
                                )
                              }}
                              isCompleted={isCompleted}
                              isTouched={isTouched}
                            >
                              <div
                                className={cn(
                                  'banner',
                                  currentStep === 0 && isReassuranceBannerVisible && 'visible',
                                )}
                              >
                                <ReassuranceBanner
                                  description={<Text>{reassuranceBannerDescription}</Text>}
                                  onClose={() => setIsReassuranceBannerVisible(false)}
                                />
                              </div>
                              {content}
                            </FormStep>
                          )
                        })}
                      </div>
                    }
                    sidebar={<Summary />}
                  />
                  {currentStep > 1 && <BeforeCloseModal />}
                  {!affiliate && (
                    <MultiCountryDiscountPopup
                      isActive={currentStep === 1 && values.jurisdictions.length > 0}
                      isClosed={closedPopups.includes('discount')}
                      setIsClosed={() => setClosedPopups(addClosedPopups(closedPopups, 'discount'))}
                    />
                  )}
                  <FormErrorScroll />
                </Form>
              )
            }}
          </Formik>
        </Page>
      </MarketsModalProvider>

      <style jsx>{`
        .banner {
          opacity: 0;
          visibility: hidden;
          max-height: 0;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out;
        }
        .banner.visible {
          opacity: 1;
          visibility: visible;
          max-height: 350px;
          margin-bottom: ${theme.spacing[6]};
        }
        .mobile {
          display: none;
        }
        .demo-description {
          margin-top: ${theme.spacing[3]};
        }
        .underline {
          color: ${theme.palette.info.main};
          text-decoration: underline;
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .desktop {
            display: none;
          }
          .mobile {
            display: block;
          }
        }
      `}</style>
    </>
  )
}

export default VerificationForm
